import type {
  OverlayDoc,
  OverlayItem,
  OverlaySection,
  OverlayTextItem,
} from "~/ui-rtk/api/adForgeApi"

/**
 * The layout solver: turns the flow document (sections/items, no
 * coordinates) into absolute boxes in canvas space. It is THE single source
 * of geometry — the DOM editor renders divs from these boxes and the canvas
 * exporter draws the same boxes, so what you edit is exactly what exports.
 *
 * All units are px in `doc.canvas` space; callers scale for display.
 */

export interface MeasuredText {
  /** Wrapped lines (uppercase already applied). */
  lines: string[]
  /** Widest line width, px. */
  blockWidth: number
}

/** Measures wrapped text at doc-space font size (canvas measureText). */
export type TextMeasurer = (
  item: OverlayTextItem,
  maxWidth: number,
) => MeasuredText

export interface SolvedTextBox {
  kind: "text"
  item: OverlayTextItem
  sectionId: string
  x: number
  y: number
  width: number
  height: number
  /** Text block position inside the box (excludes background padding). */
  textX: number
  textY: number
  textWidth: number
  lines: string[]
  lineHeightPx: number
}

export interface SolvedBlockBox {
  kind: "logo" | "divider" | "spacer"
  item: OverlayItem
  sectionId: string
  x: number
  y: number
  width: number
  height: number
}

export interface SolvedSectionBand {
  kind: "band"
  section: OverlaySection
  x: number
  y: number
  width: number
  height: number
}

export type SolvedBox = SolvedTextBox | SolvedBlockBox

export interface SolvedLayout {
  /** Painted first (behind items), in section order. */
  bands: SolvedSectionBand[]
  /** Painted in document order. */
  boxes: SolvedBox[]
}

interface ItemSize {
  width: number
  height: number
  measured?: MeasuredText
  lineHeightPx?: number
}

function measureItem(
  item: OverlayItem,
  contentWidth: number,
  measureText: TextMeasurer,
  isRow: boolean,
): ItemSize {
  if (item.type === "text") {
    const maxWidth = Math.max(
      8,
      (contentWidth * (item.maxWidthPct ?? 100)) / 100 -
        2 * (item.background?.paddingX ?? 0),
    )
    const measured = measureText(item, maxWidth)
    const lineHeightPx = item.fontSize * item.lineHeight
    const padX = item.background?.paddingX ?? 0
    const padY = item.background?.paddingY ?? 0
    return {
      width: measured.blockWidth + 2 * padX,
      height: measured.lines.length * lineHeightPx + 2 * padY,
      measured,
      lineHeightPx,
    }
  }
  if (item.type === "logo" || item.type === "divider") {
    return { width: item.width, height: item.height }
  }
  // Spacer: occupies space along the main axis only.
  return isRow
    ? { width: item.size, height: 0 }
    : { width: 0, height: item.size }
}

export function solveOverlay(
  doc: OverlayDoc,
  measureText: TextMeasurer,
): SolvedLayout {
  const { width: canvasW, height: canvasH } = doc.canvas
  const bands: SolvedSectionBand[] = []
  const boxes: SolvedBox[] = []

  for (const section of doc.sections) {
    const contentWidth = Math.max(8, canvasW - 2 * section.paddingX)
    const isRow = section.direction === "row"
    // Items the designer dragged to an absolute spot float OUT of the flow:
    // they render at their own position and take no space in the stack.
    const flowItems = section.items.filter(item => !item.position)
    const floatingItems = section.items.filter(item => item.position)
    const sizes = flowItems.map(item =>
      measureItem(item, contentWidth, measureText, isRow),
    )
    const gaps = Math.max(0, flowItems.length - 1) * section.gap

    // Section extent along the main axis.
    const stackHeight = isRow
      ? Math.max(...sizes.map(s => s.height), 0)
      : sizes.reduce((sum, s) => sum + s.height, 0) + gaps

    // Vertical anchor.
    const startY =
      section.anchor === "top"
        ? section.paddingY
        : section.anchor === "bottom"
          ? canvasH - section.paddingY - stackHeight
          : (canvasH - stackHeight) / 2

    if (section.background) {
      // Full-width band. Anchored sections read as real headers/footers:
      // a top band runs from the canvas's TOP edge (y=0) down past the
      // stack, a bottom band from above the stack to the BOTTOM edge
      // (y=canvasH). Center bands hug the stack with a little padding.
      const bandPad = Math.min(section.paddingY, section.gap + 16)
      const top = section.anchor === "top" ? 0 : startY - bandPad
      const bottom =
        section.anchor === "bottom" ? canvasH : startY + stackHeight + bandPad
      bands.push({
        kind: "band",
        section,
        x: 0,
        y: top,
        width: canvasW,
        height: bottom - top,
      })
    }

    const placeItem = (
      item: OverlayItem,
      size: ItemSize,
      x: number,
      y: number,
    ): void => {
      if (item.type === "text" && size.measured && size.lineHeightPx) {
        const padX = item.background?.paddingX ?? 0
        const padY = item.background?.paddingY ?? 0
        boxes.push({
          kind: "text",
          item,
          sectionId: section.id,
          x,
          y,
          width: size.width,
          height: size.height,
          textX: x + padX,
          textY: y + padY,
          textWidth: size.measured.blockWidth,
          lines: size.measured.lines,
          lineHeightPx: size.lineHeightPx,
        })
      } else if (item.type !== "text") {
        boxes.push({
          kind: item.type,
          item,
          sectionId: section.id,
          x,
          y,
          width: size.width,
          height: size.height,
        })
      }
    }

    if (isRow) {
      const rowWidth = sizes.reduce((sum, s) => sum + s.width, 0) + gaps
      let cursorX: number
      let extraGap = 0
      if (section.justify === "space-between" && flowItems.length > 1) {
        cursorX = section.paddingX
        extraGap =
          (contentWidth - sizes.reduce((sum, s) => sum + s.width, 0)) /
          (flowItems.length - 1)
      } else if (section.justify === "center") {
        cursorX = section.paddingX + (contentWidth - rowWidth) / 2
      } else if (section.justify === "end") {
        cursorX = section.paddingX + contentWidth - rowWidth
      } else {
        cursorX = section.paddingX
      }
      flowItems.forEach((item, i) => {
        const size = sizes[i]
        // Vertically center items within the row.
        const y = startY + (stackHeight - size.height) / 2
        placeItem(item, size, cursorX, y)
        cursorX +=
          size.width +
          (section.justify === "space-between" && flowItems.length > 1
            ? extraGap
            : section.gap)
      })
    } else {
      let cursorY = startY
      flowItems.forEach((item, i) => {
        const size = sizes[i]
        const x =
          section.align === "center"
            ? section.paddingX + (contentWidth - size.width) / 2
            : section.align === "end"
              ? section.paddingX + contentWidth - size.width
              : section.paddingX
        placeItem(item, size, x, cursorY)
        cursorY += size.height + section.gap
      })
    }

    // Floating items: absolute placement at their dragged position
    // (percentages of the canvas → px in this doc's space).
    for (const item of floatingItems) {
      if (!item.position) continue
      const size = measureItem(item, contentWidth, measureText, isRow)
      placeItem(
        item,
        size,
        (item.position.xPct / 100) * canvasW,
        (item.position.yPct / 100) * canvasH,
      )
    }
  }

  return { bands, boxes }
}

export const fontFamiliesInDoc = (doc: OverlayDoc): string[] =>
  doc.sections.flatMap(section =>
    section.items
      .filter((item): item is OverlayTextItem => item.type === "text")
      .map(item => item.fontFamily),
  )
